import noUiSlider from 'nouislider';

const SELECTOR = {
  slider: '.slider',
  container: '.slider__container',
  minContainer: '.slider__min-value',
  maxContainer: '.slider__max-value'
};

const numberFormats = {
  thai: 'th'
};

export const DEFAULT_OPTIONS = {
  selector: SELECTOR
};

class Slider {
  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node, to add slider properties on.
   * */
  constructor(elementRef) {
    // Initialise attributes

    this.elementRef = elementRef;
    this.sliderContainer = elementRef.querySelector(SELECTOR['container']);
    this.sliderMinContainer = elementRef.querySelector(SELECTOR['minContainer']);
    this.sliderMaxContainer = elementRef.querySelector(SELECTOR['maxContainer']);

    this.numberFormat = elementRef.dataset.numberFormat || numberFormats.thai;
    this.numberFormatter = new Intl.NumberFormat(this.numberFormat);

    // Bind functions
    this.changeHandler = this.changeHandler.bind(this);
    this.updateHandler = this.updateHandler.bind(this);

    this._setup();
  }

  // Event Handlers

  /**
   * Event handler when the slider value has changed.
   *  Trigerred at the end of drag
   *
   * @param {Array} _values - Current slider values
   * @param {Number} _handle - Index of handle that caused the event
   * @param {Array} unencoded - Current slider values
   * @param {Boolean} _tap - Event was caused by the user tapping the slider
   * @param {Array} _positions - Left offset of the handles in relation to the slider
   * */
  changeHandler(_values, _handle, unencoded, _tap, _positions) {
    let changeEvent = new Event('change');
    this.elementRef.dispatchEvent(changeEvent);
  }

  /**
   * Event handler when the slider value has updated.
   *
   * @param {Array} _values - Current slider values
   * @param {Number} _handle - Index of handle that caused the event
   * @param {Array} unencoded - Current slider values
   * @param {Boolean} _tap - Event was caused by the user tapping the slider
   * @param {Array} _positions - Left offset of the handles in relation to the slider
   * */
  updateHandler(_values, _handle, unencoded, _tap, _positions) {
    let [minValue, maxValue] = unencoded;

    this.sliderMinContainer.innerText = this.numberFormatter.format(minValue);
    this.sliderMaxContainer.innerText = this.numberFormatter.format(maxValue);
  }

  // Private

  /**
   * Checks if the provided slider range is valid or not.
   *  Checks:
   *    - Minimum range value MUST be less than the Maximum range value.
   *    - Minimum slider value MUST be less than or equal to the Maximum slider value.
   *
   * @return {Boolean}
   * */
  _isRangeValid() {
    let [rangeMin, rangeMax, sliderStart, sliderEnd] = this._rangeValue();

    return (
      (rangeMin < rangeMax) &&
      (sliderStart <= sliderEnd)
    );
  }

  /**
   * Reads the range values from the data attributes and converts them to integer.
   *
   * @return {Array} - an array of range values.
   * */
  _rangeValue() {
    return (
      Object.values(this.sliderContainer.dataset).map(x => {
        return parseInt(x);
      })
    );
  }

  /**
   * Set-up the slider.
   * */
  _setup() {
    let [rangeMin, rangeMax, sliderStart, sliderEnd] = this._rangeValue();

    noUiSlider.create(this.sliderContainer, {
      start: [
        (sliderStart >= rangeMin) ? sliderStart : rangeMin,
        (sliderEnd <= rangeMax) ? sliderEnd : rangeMax
      ],
      step: 1,
      range: {
        'min': [rangeMin],
        'max': [rangeMax]
      }
    });

    !this._isRangeValid() && this.sliderContainer.setAttribute('disabled', true);

    this._addEventListeners();
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.sliderContainer.noUiSlider.on('end', this.changeHandler);
    this.sliderContainer.noUiSlider.on('update', this.updateHandler);
  }
}

export default Slider;
